import { type SupportedLocaleMap } from '@zep-us/i18n';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import '../tracking/events.quiz-landing';
import { trackEvent } from '@zep/logger/tracking';
import { useLocale } from '@/hooks';
import { ChevronRightIcon } from '@/icons';
import { FunctionCard, FunctionCards } from '@/shared/components';
import { Button } from '@/ui_v3';
const FEATURE_1_IMAGE_LINKS: SupportedLocaleMap<'ko' | 'en' | 'ja'> = {
  ko: '/assets/landing-board/collab/ko.webp',
  en: '/assets/landing-board/collab/en.webp',
  ja: '/assets/landing-board/collab/ja.webp'
};
const FEATURE_2_IMAGE_LINKS: SupportedLocaleMap<'ko' | 'en' | 'ja'> = {
  ko: '/assets/landing-board/unlimited/ko.webp',
  en: '/assets/landing-board/unlimited/en.webp',
  ja: '/assets/landing-board/unlimited/ja.webp'
};
const FEATURE_3_IMAGE_LINKS: SupportedLocaleMap<'ko' | 'en' | 'ja'> = {
  ko: '/assets/landing-board/attachment/ko.webp',
  en: '/assets/landing-board/attachment/en.webp',
  ja: '/assets/landing-board/attachment/ja.webp'
};
export const Board = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  return <section className="px-[16px] py-2xl min-[751px]:px-[24px] min-[1025px]:px-[40px] min-[1025px]:py-3xl" data-sentry-component="Board" data-sentry-source-file="Board.tsx">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-lg min-[751px]:items-stretch min-[751px]:gap-2xl min-[1025px]:gap-3xl">
        <div className="flex flex-col items-center gap-md px-[16px] min-[751px]:flex-row min-[751px]:items-center min-[751px]:justify-between">
          <h2 className="text-center text-display-sm-extrabold text-default min-[751px]:text-left">
            {t('landingV3.board.title')}
          </h2>
          <MoreButton data-sentry-element="MoreButton" data-sentry-source-file="Board.tsx" />
        </div>
        <FunctionCards topElement={<Feature1 />} bottomLeftElement={<Feature2 />} bottomRightElement={<Feature3 />} data-sentry-element="FunctionCards" data-sentry-source-file="Board.tsx" />
      </div>
    </section>;
};
const MoreButton = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  return <Button variant="outlined" intent="alternative" size="lg" rounded="default" asChild data-sentry-element="Button" data-sentry-component="MoreButton" data-sentry-source-file="Board.tsx">
      <Link href="/board" data-testid="landing-board-more" onClick={() => trackEvent('landing_board_more_click', {})} data-sentry-element="Link" data-sentry-source-file="Board.tsx">
        {t('landingV3.board.moreButton')}
        <ChevronRightIcon size="md" data-sentry-element="ChevronRightIcon" data-sentry-source-file="Board.tsx" />
      </Link>
    </Button>;
};
const Feature1 = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  const {
    locale
  } = useLocale();
  return <FunctionCard.HorizontalWrapper className="bg-violet-50" data-sentry-element="unknown" data-sentry-component="Feature1" data-sentry-source-file="Board.tsx">
      <div className="flex-1">
        <div className="flex size-full flex-col items-center gap-sm px-lg py-xl min-[751px]:items-stretch min-[751px]:gap-lg min-[751px]:p-2xl min-[751px]:pr-0 min-[1025px]:p-3xl min-[1025px]:pr-0">
          <FunctionCard.Title className="whitespace-pre-line text-violet-800" data-sentry-element="unknown" data-sentry-source-file="Board.tsx">
            {t('landingV3.boardFeature1.title')}
          </FunctionCard.Title>
          <FunctionCard.Description className="whitespace-pre-line" data-sentry-element="unknown" data-sentry-source-file="Board.tsx">
            {t('landingV3.boardFeature1.description')}
          </FunctionCard.Description>
        </div>
      </div>

      <div className="flex-1 min-[751px]:h-[310px] min-[1025px]:h-[374px]">
        <img src={FEATURE_1_IMAGE_LINKS[locale] ?? FEATURE_1_IMAGE_LINKS.en} alt={t('landingV3.boardFeature1.imageAlt')} width={624} height={374} loading="lazy" className="size-full min-[751px]:object-cover min-[751px]:object-left" />
      </div>
    </FunctionCard.HorizontalWrapper>;
};
const Feature2 = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  const {
    locale
  } = useLocale();
  return <FunctionCard.VerticalWrapper className="bg-violet-100" data-sentry-element="unknown" data-sentry-component="Feature2" data-sentry-source-file="Board.tsx">
      <div className="flex flex-col items-center gap-sm px-lg py-xl min-[751px]:items-stretch min-[751px]:gap-lg min-[751px]:p-2xl min-[751px]:pb-0 min-[1025px]:p-3xl min-[1025px]:pb-0">
        <FunctionCard.Title className="whitespace-pre-line text-violet-900" data-sentry-element="unknown" data-sentry-source-file="Board.tsx">
          {t('landingV3.boardFeature2.title')}
        </FunctionCard.Title>
        <FunctionCard.Description className="whitespace-pre-line" data-sentry-element="unknown" data-sentry-source-file="Board.tsx">
          {t('landingV3.boardFeature2.description')}
        </FunctionCard.Description>
      </div>

      <img src={FEATURE_2_IMAGE_LINKS[locale] ?? FEATURE_2_IMAGE_LINKS.en} alt={t('landingV3.boardFeature2.imageAlt')} width={620} height={230} loading="lazy" className="h-auto w-full" />
    </FunctionCard.VerticalWrapper>;
};
const Feature3 = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  const {
    locale
  } = useLocale();
  return (
    // Figma 는 이 카드만 violet-50 위에 흰색 40%를 얹은 합성값(raw #f7f7ff)을 바인딩했다
    // — violet-50(#f3f2ff)와도 일치하지 않아 시맨틱 토큰이 없다. BoardLandingFeatures.tsx
    // QrCard 와 같은 상황.
    <FunctionCard.VerticalWrapper className="bg-[#F7F7FF]" data-sentry-element="unknown" data-sentry-component="Feature3" data-sentry-source-file="Board.tsx">
      <div className="flex flex-col items-center gap-sm px-lg py-xl min-[751px]:items-stretch min-[751px]:gap-lg min-[751px]:p-2xl min-[751px]:pb-0 min-[1025px]:p-3xl min-[1025px]:pb-0">
        <FunctionCard.Title className="whitespace-pre-line text-violet-900" data-sentry-element="unknown" data-sentry-source-file="Board.tsx">
          {t('landingV3.boardFeature3.title')}
        </FunctionCard.Title>
        <FunctionCard.Description className="whitespace-pre-line" data-sentry-element="unknown" data-sentry-source-file="Board.tsx">
          {t('landingV3.boardFeature3.description')}
        </FunctionCard.Description>
      </div>

      <img src={FEATURE_3_IMAGE_LINKS[locale] ?? FEATURE_3_IMAGE_LINKS.en} alt={t('landingV3.boardFeature3.imageAlt')} width={620} height={230} loading="lazy" className="h-auto w-full" />
    </FunctionCard.VerticalWrapper>
  );
};